import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "../../app/trpc.js";
import { Button, Chip, Input, Label, Notice, Sheet } from "../../ui/index.js";
import { addDays, dayStart, useCabinet } from "../cabinet/index.js";
import { AffectedContacts } from "./AffectedContacts.js";
import { invalidateBookings } from "./invalidate.js";

type Kind = "sick" | "vacation";

/**
 * Больничный или отпуск на дни: закрывает график поверх записей и показывает, чьи записи это затронуло
 * и как связаться с каждым клиентом. Записи сами не отменяются, мастер решает с клиентом.
 */
export function TimeOffSheet({ open, onClose, day, defaultMasterId }: { open: boolean; onClose: () => void; day: string; defaultMasterId: string | null }) {
  const { t } = useTranslation();
  const cab = useCabinet();
  const [masterId, setMasterId] = useState<string | null>(defaultMasterId);
  const [kind, setKind] = useState<Kind>("sick");
  const [from, setFrom] = useState(day);
  const [to, setTo] = useState(day);
  const masters = useQuery(trpc.calendar.masters.list.queryOptions({ salonId: cab.salonId }, { enabled: open }));
  const set = useMutation(trpc.calendar.timeOff.set.mutationOptions({ onSuccess: () => invalidateBookings() }));
  const { reset } = set;

  useEffect(() => {
    if (open) {
      setFrom(day);
      setTo(day);
      setMasterId(defaultMasterId);
      reset();
    }
  }, [open, day, defaultMasterId, reset]);

  const submit = () => {
    if (!masterId || to < from) return;
    set.mutate({ masterId, kind, from: dayStart(from, cab.timezone), to: dayStart(addDays(to, 1), cab.timezone) });
  };

  return (
    <Sheet open={open} onClose={onClose} title={t("cal.timeOffTitle")}>
      {set.data ? (
        <div className="space-y-3">
          <Notice kind="success">{set.data.length > 0 ? t("cal.affected", { count: set.data.length }) : t("cal.noAffected")}</Notice>
          {set.data.length > 0 && <AffectedContacts bookingIds={set.data.map((b) => b.id)} onNavigate={onClose} />}
          <Button variant="secondary" className="w-full" onClick={onClose}>
            {t("common.done")}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {cab.isOwner && (
            <div className="-mx-4 flex gap-2 overflow-x-auto px-4">
              {masters.data?.map((m) => (
                <Chip key={m.id} active={masterId === m.id} onClick={() => setMasterId(m.id)}>
                  {m.name}
                </Chip>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            {(["sick", "vacation"] as const).map((k) => (
              <Chip key={k} active={kind === k} onClick={() => setKind(k)}>
                {t(`cal.offKind.${k}`)}
              </Chip>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="off-from">{t("cal.fromDay")}</Label>
              <Input id="off-from" type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} />
            </div>
            <div className="flex-1">
              <Label htmlFor="off-to">{t("cal.toDay")}</Label>
              <Input id="off-to" type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} />
            </div>
          </div>
          <p className="text-sm text-slate-500">{t("cal.timeOffHint")}</p>
          {set.isError && <Notice kind="error">{t("common.error")}</Notice>}
          <Button className="w-full" disabled={!masterId || to < from || set.isPending} onClick={submit}>
            {t("cal.closeDays")}
          </Button>
        </div>
      )}
    </Sheet>
  );
}
